import { FC, ReactNode, useEffect, useState } from 'react';

import { tokenStorage } from 'api/client/helpers/tokenStorage';
import { AccessTokenType } from 'api/types/data';
import { LoadingLayoutWrapper } from 'components';
import { ROUTES, useNavigate } from 'libs/navigation';
import { useUser } from 'libs/providers';

interface ChildProps {
  isAccountPages: boolean;
  isAuthPage: boolean;
  isOnboardingPage: boolean;
  isTempPasswordPage: boolean;
  isChangePasswordPage: boolean;
  isMainPage: boolean;
  isLoginPage: boolean;
}

export const NavigationMiddleware: FC<{
  children: (props: ChildProps) => ReactNode;
}> = ({ children }) => {
  const { user, client } = useUser();
  const { navigate, paths } = useNavigate();

  const isMainPage = paths.length === 2 && paths[1] === '/';

  const isLoginPage =
    paths.includes(ROUTES.login.fullPath) ||
    paths.includes(ROUTES.loginByToken.fullPath);

  const isRegistrationPage = paths.includes(ROUTES.registration.fullPath);

  const isOnboardingPage = paths.some((v) =>
    v.startsWith(ROUTES.onboarding.fullPath)
  );

  const isRestorePassword = paths.includes(ROUTES.restorePassword.fullPath);
  const isAuthPage = isLoginPage || isRegistrationPage || isRestorePassword;

  const isTempPasswordPage = !!user?.isPasswordUpdateRequired;

  const isChangePasswordPage = paths.includes(ROUTES.changePassword.fullPath);

  const isAgreementPage = paths.includes(ROUTES.agreement.fullPath);

  const isAccountPages = paths.includes(ROUTES.account.fullPath);

  const isAccountOpened = !!client?.isAccountOpened;

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const navigateTo = () => {
      if (!user) {
        if (!isAuthPage) {
          navigate(
            ROUTES.login.fullPath,
            {},
            { search: { ret: window.location.href } }
          );
        }
        return;
      }

      // otp verification or password change is required
      if (!user.otp?.verified || isTempPasswordPage) {
        navigate(ROUTES.registration.fullPath, {});
        return;
      }

      if (
        tokenStorage.getAccessTokenType() ===
          AccessTokenType.AgreementAcceptanceRequired &&
        !isAgreementPage
      ) {
        navigate(
          ROUTES.agreement.fullPath,
          {},
          { search: { ret: window.location.href } }
        );
        return;
      }

      if (isOnboardingPage || isChangePasswordPage || isAgreementPage) {
        return;
      }

      if (!isAccountOpened) {
        navigate(ROUTES.onboarding.fullPath, {});
        return;
      }

      if (!isAccountPages) {
        navigate(ROUTES.account.fullPath, {});
      }
    };
    navigateTo();
    setLoading(false);
  }, [
    navigate,
    user,
    isAuthPage,
    isOnboardingPage,
    isTempPasswordPage,
    isChangePasswordPage,
    isAccountOpened,
    isAccountPages,
    isAgreementPage,
  ]);

  return (
    <LoadingLayoutWrapper loading={loading}>
      {children({
        isAccountPages: isAccountOpened,
        isAuthPage,
        isOnboardingPage,
        isTempPasswordPage,
        isChangePasswordPage,
        isMainPage,
        isLoginPage,
      })}
    </LoadingLayoutWrapper>
  );
};
